<script setup>
import { ref, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useDirectoryDetailStore } from "@/modules/directory/stores/directoryDetail";

const route = useRoute();
const router = useRouter();
const detailStore = useDirectoryDetailStore();

const productQuery = ref("");
const nandinaQuery = ref("");

// Cargar datos al montar
onMounted(() => {
  detailStore.fetchCompanyDetail(route.params.id);
});

// Navegar al listado general
const goToDirectory = () => {
  router.push({ name: "directory-home" });
};

// Buscar producto
const handleSearchProduct = (e) => {
  e.preventDefault();
  detailStore.searchByProductName(productQuery.value);
};

// Buscar por NANDINA
const handleSearchNandina = (e) => {
  e.preventDefault();
  detailStore.searchByNandina(nandinaQuery.value);
};

// Reactividad: si se limpian los buscadores, se restauran los productos
watch([productQuery, nandinaQuery], ([name, nandina]) => {
  if (!name.trim() && !nandina.trim()) {
    detailStore.resetFilters();
  }
});
</script>

<template>
  <!-- 🌿 Banner -->
  <section class="pt-0">
    <div class="container-fluid position-relative">
      <div class="row">
        <div class="col-12">
          <div
            class="card overflow-hidden h-100px h-xl-200px rounded-3 position-relative"
            style="
              background-image: url(/assets/images/bg/fondo-acercade-viaverde.jpg);
              background-position: center left;
              background-size: cover;
            "
          >
            <div class="bg-overlay bg-dark opacity-6"></div>

            <!-- Botón Volver (solo desktop) -->
            <button
              @click="goToDirectory"
              class="btn btn-green btn-sm rounded-pill px-3 position-absolute d-none d-md-block"
              style="top: 20px; left: 20px; z-index: 20"
            >
              <i class="fas fa-arrow-left me-2"></i> Volver al directorio
            </button>

            <!-- Overlay de título -->
            <div
              class="card-img-overlay d-flex align-items-end flex-column justify-content-end"
            >
              <div class="w-100 p-3 p-sm-4">
                <h2 class="text-white fw-semibold mb-1">
                  {{ detailStore.company?.name || "Cargando..." }}
                </h2>
                <p class="text-white-50 mb-0">
                  Empresa registrada en Vía Verde
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- 🔹 Botón alternativo para móviles -->
  <div class=" d-md-none text-center">
    <button
      @click="goToDirectory"
      class="btn btn-sm btn-green rounded-pill px-4 fw-semibold"
    >
      <i class="fas fa-arrow-left me-2"></i> Volver al directorio
    </button>
  </div>

  <!-- 🌿 Contenido principal -->
  <div class="container py-5">
    <div v-if="detailStore.loading" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <div v-else-if="detailStore.error" class="text-center py-5 text-danger">
      <p>{{ detailStore.error }}</p>
    </div>

    <div v-else class="row g-5">
      <!-- 📄 Información de empresa -->
      <div class="col-xl-8">
        <h4 class="text-success mb-4">Datos de la empresa</h4>

        <div class="row g-4">
          <div class="col-md-6">
            <div class="card card-body shadow p-4">
              <ul class="list-group list-group-borderless datos">
                <li
                  class="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span class="h6 fw-light mb-0">
                    <i class="fas fa-user text-success me-2"></i>Contacto
                  </span>
                  <span>{{
                    detailStore.company?.contact_name || "Sin información"
                  }}</span>
                </li>
                <li
                  class="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span class="h6 fw-light mb-0">
                    <i class="fas fa-envelope text-success me-2"></i>Correo
                  </span>
                  <span>{{
                    detailStore.company?.email || "Sin información"
                  }}</span>
                </li>
                <li
                  class="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span class="h6 fw-light mb-0">
                    <i class="fas fa-phone text-success me-2"></i>Teléfono
                  </span>
                  <span>{{
                    detailStore.company?.phone_number || "Sin información"
                  }}</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="col-md-6">
            <div class="card card-body shadow p-4">
              <ul class="list-group list-group-borderless datos">
                <li
                  class="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span class="h6 fw-light mb-0">
                    <i class="fas fa-map-marker-alt text-success me-2"></i
                    >Departamento
                  </span>
                  <span>{{
                    detailStore.company?.department || "Sin información"
                  }}</span>
                </li>
                <li
                  class="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span class="h6 fw-light mb-0">
                    <i class="fas fa-briefcase text-success me-2"></i>Sector
                  </span>
                  <span>{{
                    detailStore.company?.sector || "Sin información"
                  }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- 🧾 Productos -->
        <div class="mt-5">
          <h4 class="text-success mb-4">
            Productos registrados
            <span class="cantidad-productos"
              >({{ detailStore.filteredProducts.length }})</span
            >
          </h4>

          <div
            v-if="detailStore.filteredProducts.length === 0"
            class="text-center text-muted py-5"
          >
            <p>No se encontraron productos para esta empresa.</p>
          </div>

          <div
            v-for="product in detailStore.paginatedProducts"
            :key="product.id"
            class="card shadow-sm overflow-hidden mb-4 border-0"
          >
            <div class="row g-0">
              <div class="col-md-4 overflow-hidden">
                <img
                  :src="
                    product.image_path
                      ? product.image_path
                      : '/assets/images/placeholders/product-placeholder.jpg'
                  "
                  class="rounded-start w-100 product-image"
                  :alt="product.name"
                  :title="product.name"
                />
              </div>
              <div class="col-md-8">
                <div class="card-body">
                  <h5 class="card-title fw-semibold text-dark">
                    {{ product.name || "Sin información" }}
                  </h5>
                  <p class=" mb-2 detalle">
                    <strong>Detalle:</strong>
                    {{ product.description || "Sin información" }}
                  </p>
                  <p class=" mb-2 detalle">
                    <strong>NANDINA:</strong>
                    {{ product.productonacional?.nandina || "Sin información" }}
                  </p>
                  <p class=" mb-0 detalle">
                    <strong>Descripción:</strong>
                    {{
                      product.productonacional?.desnandina || "Sin información"
                    }}
                  </p>
                  
                </div>
              </div>
            </div>
          </div>

          <!-- 🌿 Paginación -->
          <nav
            v-if="detailStore.totalPages > 1"
            class="mt-4 d-flex justify-content-center"
          >
            <ul class="pagination">
              <li
                class="page-item"
                :class="{ disabled: detailStore.currentPage === 1 }"
              >
                <button
                  class="page-link"
                  @click="detailStore.changePage(detailStore.currentPage - 1)"
                >
                  Anterior
                </button>
              </li>

              <li
                v-for="page in detailStore.totalPages"
                :key="page"
                class="page-item"
                :class="{ active: detailStore.currentPage === page }"
              >
                <button class="page-link" @click="detailStore.changePage(page)">
                  {{ page }}
                </button>
              </li>

              <li
                class="page-item"
                :class="{
                  disabled: detailStore.currentPage === detailStore.totalPages,
                }"
              >
                <button
                  class="page-link"
                  @click="detailStore.changePage(detailStore.currentPage + 1)"
                >
                  Siguiente
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <!-- 🔍 Panel lateral -->
      <div class="col-xl-4">
        <div class="card card-body border p-4 shadow-lg rounded-3">
          <h5 class="mb-4 text-success">Buscar productos</h5>

          <!-- Buscar por nombre -->
          <form @submit="handleSearchProduct" class="mb-4">
            <input
              v-model="productQuery"
              class="form-control pe-5 bg-transparent"
              type="search"
              placeholder="Buscar por nombre de producto"
            />
            <button type="submit" class="btn btn-green mt-3 w-100 fw-semibold">
              Buscar producto
            </button>
          </form>

          <!-- Buscar por NANDINA -->
          <form @submit="handleSearchNandina">
            <input
              v-model="nandinaQuery"
              class="form-control pe-5 bg-transparent"
              type="search"
              placeholder="Buscar por NANDINA o descripción"
            />
            <button type="submit" class="btn btn-green mt-3 w-100 fw-semibold">
              Buscar por NANDINA
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  h4,h5{
    font-family: "Merriweather";
  }
  .datos li span{
    font-family: "Montserrat";
    text-align: left;
  }
  .detalle{
    font-family: "Montserrat";
    text-align: justify;
  }
  .cantidad-productos{
    color: rgb(127, 127, 127);
  }
.btn-green {
  background-color: #3ca94d;
  border-color: #3ca94d;
  color: white;
  transition: all 0.2s ease-in-out;
}
.btn-green:hover {
  background-color: #319040;
  border-color: #319040;
}
.text-success {
  color: #3ca94d !important;
}
.pagination .active .page-link {
  background-color: #3ca94d;
  border-color: #3ca94d;
  color: white;
}
.container {
  padding-top: 2rem;
  padding-bottom: 2rem;
}

/* 🔹 Altura uniforme para las imágenes */
.product-image {
  height: 220px;
  object-fit: cover;
  -webkit-border-radius: 10px;
border-radius: 10px;
}

/* Responsivo */
@media (max-width: 991px) {
  .product-image {
    height: 180px;
  }
}

/* 🔹 Corrige altura mínima del banner para mostrar el texto completo en móvil */
@media (max-width: 768px) {
  .card.h-100px.h-xl-200px {
    height: auto !important;
    min-height: 140px; /* suficiente para que el título no se corte */
    padding-top: 10px;
    padding-bottom: 10px;
  }
}

</style>
